/**
 * Contacts — plan §6.2's list screen, and the one §3.5 names as the checkpoint
 * for the list engine: "DataList supports filter/sort/group/fields/Σ/saved
 * views/export/import on Contacts".
 *
 * Everything on this page beyond the four things below comes from the engine.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { ListScreen } from '../components/list-screen.js'
import { BulkRecordPicker, type BulkAction } from '../components/bulk-menu.js'
import { MenuSelect } from '../components/menu-select.js'
import { ConfirmDialog } from '../components/overlays.js'
import { RowTray } from '../components/row-tray.js'
import { contactMark } from '../components/landing-marks.js'
import { lastActivity, text } from '../components/card-line.js'
import { StatusChip } from '../components/primitives.js'
import type { CardLine } from '../components/data-list.js'
import { useList, type ListRow, type UseListResult } from '../lib/use-list.js'
import { openItems, copyItem } from '../lib/row-menu.js'
import { IconCompany, IconPerson, IconSms } from '../frame/icons.js'
import { NewSplitButton } from '../requests/new-split-button.js'

export function Contacts({ me, onNew, onNewRequest, reloadSignal }: {
  me: MeResponse
  onNew?: () => void
  /**
   * New request beside the page's own New (`new-request.md` §13.1), absent
   * for somebody who can't take one — then the plain button stays.
   */
  onNewRequest?: () => void
  /** Bumped after New request creates something, so the list shows it (§11.4). */
  reloadSignal?: number
}) {
  // The metric cards count these, so they are fetched whether or not the
  // Fields menu is showing them. `photoFileId` is the face on the row.
  const list = useList('contacts', {
    alwaysFetch: [
      'phone', 'email', 'photoFileId',
      /* The card's third row — see `contactCardLine`. */
      'hasPortalAccess', 'accountName', 'title', 'lastActivityAt',
    ],
  })
  const navigate = useNavigate()
  /* The list is the page's, so the page reloads it — the engine is not changed (§11.4). */
  useEffect(() => { if (reloadSignal) list.reload() }, [reloadSignal])
  const [deactivating, setDeactivating] = useState<ListRow | null>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = me.permissions.contacts === 'edit' || me.permissions.contacts === 'full'

  return (
    <>
      <ListScreen
        area="contacts"
        title="Contacts"
        list={list}
        bulk={canEdit ? { actions: CONTACT_BULK(list) } : undefined}
        actions={onNew || onNewRequest
          ? (
            <NewSplitButton label="New contact" icon={IconPerson} sub="just the contact — the full form"
              phoneSub="just the contact" onNew={onNew} onNewRequest={onNewRequest} />
          )
          : undefined}
        primaryColumn={{
          field: 'name',
          header: 'Name',
          /*
           * **How to ring them**, under the name (§3.5's "bold primary line
           * and an 11px muted second line").
           *
           * The phone number rather than the company: a contact list is read
           * to get hold of somebody, and the company has moved down to the
           * third row where it sits beside the branch and the date as one of
           * the facts *about* this person rather than as their identity.
           */
          render: (row: ListRow) => ({
            primary: String(row.name ?? ''),
            secondary: [row.phone, row.email].filter(Boolean).join(' · ') || undefined,
          }),
        }}
        cardLine={contactCardLine}
        // A face, or the initials — v2 principle 1, "read by shape first".
        mark={{ render: contactMark }}
        /*
         * One action, not two. The v2 plan asks for "its two most likely
         * actions", and for a contact the second one — message them, start a job
         * for them — has no screen behind it yet. A button that lands nowhere is
         * worse than a button that is not there, and the tray's own lines already
         * jump to the properties and the jobs.
         */
        tray={(row) => (
          <RowTray
            area="contacts"
            id={row.id}
            actions={[
              { key: 'open', label: 'Open contact', primary: true,
                onSelect: () => navigate(`/contacts/${row.id}`) },
            ]}
          />
        )}
        rowMenu={(row) => [
          ...openItems(`/contacts/${row.id}`, navigate),
          ...copyItem('copy-email', 'Copy email address', row.email, true),
          ...copyItem('copy-phone', 'Copy phone number', row.phone),
          {
            key: 'deactivate',
            label: 'Deactivate',
            tone: 'danger',
            separatorBefore: true,
            onSelect: () => setDeactivating(row),
          },
        ]}
        onRowClick={(row) => navigate(`/contacts/${row.id}`)}
        emptyTitle="No contacts yet"
        emptyBody="A contact is a person you deal with. Add one, or import a list you already keep."
        emptyAction={onNew ? { label: 'New contact', onClick: onNew } : undefined}
        importOrgUnitId={me.scope.selected[0]}
      />

    {/*
      * Rule 2: deactivating hides the contact from the default list and
      * changes nothing else. The dialog says so, because "Deactivate" next to
      * a red label reads like a delete to anybody who has used other software.
      */}
    {deactivating && (
      <ConfirmDialog
        title={`Deactivate ${String(deactivating.name ?? 'this contact')}?`}
        body="They drop out of the default list and stay on every job, invoice and message they are already on. You can bring them back at any time."
        confirmLabel="Deactivate"
        busyLabel="Deactivating…"
        busy={busy}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => {
          setBusy(true)
          void list.bulk('deactivate', [deactivating.id])
            .finally(() => { setBusy(false); setDeactivating(null) })
        }}
      />
    )}
    </>
  )
}

/**
 * What the Contacts list can do to a selection (v3 §3.3).
 *
 * Two changes and one way out. Both go through the engine's
 * `/bulk-actions/:key`, so they work on a `matching` selection of four
 * hundred as readily as on three ticked rows — and Export and Deactivate
 * come from the engine, which is why they are not here.
 *
 * "Copy n email addresses" from the mockup is not in yet: it is a `local`
 * action over `ctx.rows`, and on a `matching` selection `ctx.rows` is the
 * fifty that are loaded — so it would quietly copy fifty of four hundred.
 * It needs the ids resolved first, which is a round trip this PR does not
 * add.
 */
const CONTACT_BULK = (list: UseListResult): BulkAction[] => [
  {
    key: 'set_company',
    group: 'change',
    label: () => 'Set company\u2026',
    icon: IconCompany,
    picker: (_ctx, done) => (
      <BulkRecordPicker
        list={list}
        field="account"
        label="Which company?"
        clearLabel="No company"
        onPick={(accountId) => done({ accountId })}
      />
    ),
  },
  {
    key: 'set_preferred_channel',
    group: 'change',
    label: () => 'Set preferred channel\u2026',
    icon: IconSms,
    picker: (_ctx, done) => (
      <MenuSelect
        label="How should we reach them?"
        value={null}
        onChange={(channel) => done({ channel })}
        options={[
          { value: 'email', label: 'Email' },
          { value: 'sms', label: 'Text' },
          { value: 'phone', label: 'Phone' },
        ]}
        placeholder="Pick a channel"
        minWidth={240}
        className="w-full"
      />
    ),
  },
]

/**
 * A contact's third row: **[Portal on] Blue Ridge Country Club · Grounds
 * manager — Sep 26** (v3 §8).
 *
 * The chip is the one thing on a contacts list that is a *state* rather than
 * a fact — whether this person can see their own jobs and invoices — and it
 * is worth a chip precisely because it is the thing somebody is about to act
 * on. It is drawn either way: "Portal off" is the answer somebody checks
 * before sending an invite, and a chip that appears only when it is on reads
 * as a list where half the rows failed to load.
 *
 * The company and the title follow it, each dropping out when it is empty,
 * and the date lands hard right.
 */
function contactCardLine(row: ListRow): CardLine {
  const on = row.hasPortalAccess === true
  return {
    chip: (
      <StatusChip group="job" status={on ? 'approved' : 'requested'}>
        {on ? 'Portal on' : 'Portal off'}
      </StatusChip>
    ),
    facts: [text(row.accountName) || null, text(row.title) || null]
      .filter((part) => part !== null),
    trailing: lastActivity(row),
  }
}
